import React, { useRef, useEffect } from 'react';
import styles from './AltMobileMenu.module.scss';
import { useRouter } from 'next/router';
import { useAppContext } from 'Providers/App';
import Link from 'next/link';
import Carot from 'Images/brand-icons/angle-down.svg';
import Button from 'Nori/Button';
import useKeydownHandler from 'Utils/hooks/useKeyboardHandler';
import useTrapFocus from 'Utils/hooks/useTrapFocus';
import useMenuFocusLastTrigger from 'Utils/hooks/useMenuFocusLastTrigger';
import { MOBILE_MENU_CONTENT } from 'Custom/Header/Header.utils';
import { finderHandlerClick } from 'Custom/Finder/utils';
import { UI_ELEMENTS } from 'Providers/App/elements';

const {
  modalHealthcare: modalHealthcareId,
  modalISI: modalISIId,
  mobileMainMenu: mobileMainMenuId,
  mobileMenuOpen: mobileMenuOpenId,
} = UI_ELEMENTS;
const HEADER_MOBILE_HEIGHT = 82;

const MenuItem = ({ href, label, id, submenu = null, target = null }) => {
  let icon = null;
  const { setMobileActiveMenu, updateElementState } = useAppContext();

  const openSection = (id) => {
    updateElementState(mobileMainMenuId, true);
    setMobileActiveMenu(id);
  };

  if (target === '_blank') {
    icon = <ExternalLink />;
  }

  return href && !submenu ? (
    <Link
      className={styles.mobileItem}
      href={href}
      target={target}
      onClick={() => updateElementState(mobileMenuOpenId, false)}
      data-qa-tag="mobile-menu-item"
    >
      <span
        data-design-type="clickAction"
        data-design-category="top_nav_vd"
        data-design-action="Link"
        data-design-label={label}
        data-design-nori_id="Navigation"
        data-design-module_name="Navigation"
        data-design-module_no="00"
        data-qa-tag="mobile-menu-item-text"
      >
        {label}
      </span>
      {icon}
    </Link>
  ) : (
    <button
      onClick={() => openSection(id)}
      className={styles.mobileItem}
      data-menu-item-id={id}
      data-design-type="clickAction"
      data-design-category="top_nav_vd"
      data-design-action="Expand"
      data-design-nori_id="Navigation"
      data-design-module_name="Navigation"
      data-design-module_no="00"
      data-qa-tag="mobile-menu-item-button"
    >
      {label} {submenu && <Carot data-qa-tag="mobile-menu-item-carot" />}
    </button>
  );
};

const AltMobileMenu = ({ basic = false, content = MOBILE_MENU_CONTENT }) => {
  const { mainNavList, secondaryNavList } = content;
  const { pathname } = useRouter();
  const {
    getElementState,
    focusTrigger,
    updateElementState,
    getElementClickHandler,
    mobileActiveMenu,
    registerTrigger,
  } = useAppContext();

  const wrapperRef = useRef(null);
  const [mobileMenuOpen, mobileMainMenu] = getElementState(
    mobileMenuOpenId,
    mobileMainMenuId
  );
  const modalISIHandler = getElementClickHandler(modalISIId);
  const modalHealthcareHandler = getElementClickHandler(modalHealthcareId);

  const resizeObserverFn = () => {
    const height = window.innerHeight - HEADER_MOBILE_HEIGHT;
    wrapperRef.current.style.height = `${height}px`;
  };

  const closeMobileMenu = () => {
    updateElementState(mobileMenuOpenId, false);
  };

  useKeydownHandler(() => {
    closeMobileMenu();
    const menuButton = document.querySelector('#open-mobile-menu-button');
    registerTrigger(UI_ELEMENTS.menuMobile, menuButton);
    focusTrigger(UI_ELEMENTS.menuMobile);
    document.body.classList.toggle('overflowHidden', false);
  }, 'Escape');

  useTrapFocus(wrapperRef, mobileMenuOpen, [mobileMainMenu]);

  useMenuFocusLastTrigger(wrapperRef, mobileMenuOpen);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(resizeObserverFn);
    setTimeout(resizeObserverFn, 400);
    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.unobserve(document.body);
    };
  }, []);

  const menu = mainNavList.map((item, index) => {
    const { submenu = null } = item;
    const isActive =
      pathname === item.href ||
      (submenu && submenu.some((i) => i.href === pathname));

    const liClassName = `${isActive ? styles.active : ''}`;

    return (
      <li
        key={`li-${index}`}
        className={liClassName}
        data-qa-tag="mobile-menu-li"
      >
        <MenuItem {...item} />
      </li>
    );
  });

  const subMenu = mainNavList.map((item, index) => {
    const { submenu = null, id } = item;

    if (mobileActiveMenu === id && submenu) {
      const isActive =
        pathname === item.link ||
        (submenu && submenu.some((i) => i.link === pathname));

      const liClassName = `${isActive ? styles.active : ''}`;

      return (
        <ul key={`ul-${index}`}>
          {submenu.map((subitem, subindex) => (
            <li
              key={`li-${index}-${subindex}`}
              className={
                subitem.href === pathname ? `${styles.activeSubItem}` : ''
              }
              data-qa-tag="mobile-submenu-li"
            >
              <MenuItem isSubItem {...subitem} />
            </li>
          ))}
        </ul>
      );
    }

    return null;
  });

  return (
    <div
      ref={wrapperRef}
      className={styles.mobileMenu}
      tabIndex="0"
      data-qa-tag="mobile-menu-wrapper"
    >
      {mobileMainMenu ? (
        <div className={styles.subMenu} data-qa-tag="mobile-sub-menu">
          <button
            className={styles.backButton}
            id="back-button"
            data-design-type="clickAction"
            data-design-category="top_nav_vd"
            data-design-action="Close"
            data-design-nori_id="Navigation"
            data-design-module_name="Navigation"
            data-design-module_no="00"
            data-qa-tag="mobile-menu-back-button"
            onClick={(event) => {
              registerTrigger(UI_ELEMENTS.menuMobile, event.currentTarget);
              updateElementState(mobileMainMenuId, false);
            }}
          >
            <Carot /> Back
          </button>
          <div
            className={styles.subNavWrapper}
            data-qa-tag="mobile-sub-nav-wrapper"
          >
            <div
              className={styles.childItems}
              data-qa-tag="mobile-sub-menu-items"
            >
              {subMenu}
            </div>
            <div
              className={styles.subNavigation}
              data-qa-tag="mobile-sub-navigation"
            >
              <div
                className={styles.ctaBlockSub}
                data-qa-tag="mobile-cta-block"
              >
                <Button
                  onClick={(e) => {
                    closeMobileMenu();
                    if (secondaryNavList.findDoctor['data-custom-redirect']) {
                      finderHandlerClick(e);
                    }
                  }}
                  type="primaryLight"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Button"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-findaDoctor-button"
                  {...secondaryNavList.findDoctor}
                />
              </div>
              <div
                className={styles.subitemList}
                data-qa-tag="mobile-sub-item-list"
              >
                <button
                  data-trigger-mobile-id={modalISIId}
                  onClick={(event) => {
                    closeMobileMenu();
                    modalISIHandler(event);
                  }}
                  className={styles.topNavigation}
                  data-design-category="top_nav_vd"
                  data-design-type="clickAction"
                  data-design-action="Expand"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-importantSafety-button"
                  {...secondaryNavList.importantSafety}
                />
                <Link
                  onClick={() => closeMobileMenu()}
                  href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_pi.pdf"
                  target="_blank"
                  rel="noopener"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Download"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-prescribingInfo-link"
                  {...secondaryNavList.prescribingInfo}
                />

                {!basic && (
                  <button
                    data-trigger-mobile-id={modalHealthcareId}
                    onClick={(event) => {
                      modalHealthcareHandler(event);
                      closeMobileMenu();
                    }}
                    className={styles.topNavigation}
                    data-design-type="clickAction"
                    data-design-category="top_nav_vd"
                    data-design-action="Expand"
                    data-design-nori_id="Navigation"
                    data-design-module_name="Navigation"
                    data-design-module_no="00"
                    data-qa-tag="mobile-healthcare-button"
                    {...secondaryNavList.healthcare}
                  />
                )}
                <Link
                  onClick={() => closeMobileMenu()}
                  href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_ppi.pdf"
                  target="_blank"
                  rel="noopener"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Download"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-patientInfo-link"
                  {...secondaryNavList.patientInfo}
                />
                {!basic && (
                  <Link
                    onClick={() => closeMobileMenu()}
                    href="/es-us"
                    target="_blank"
                    rel="noopener"
                    data-design-type="clickAction"
                    data-design-category="top_nav_vd"
                    data-design-action="Link"
                    data-design-nori_id="Navigation"
                    data-design-module_name="Navigation"
                    data-design-module_no="00"
                    data-qa-tag="mobile-es-menu-link"
                    {...secondaryNavList.lang}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.mainSection} data-qa-tag="mobile-main-section">
          {!basic && (
            <>
              <ul className={styles.mobileList} data-qa-tag="mobile-menu-list">
                {menu}
              </ul>
              <div className={styles.ctaBlock} data-qa-tag="mobile-cta-block">
                <Button
                  onClick={(e) => {
                    closeMobileMenu();
                    if (secondaryNavList.findDoctor['data-custom-redirect']) {
                      finderHandlerClick(e);
                    }
                  }}
                  type="primaryLight"
                  data-design-type="clickAction"
                  data-design-category="top_nav_vd"
                  data-design-action="Button"
                  data-design-nori_id="Navigation"
                  data-design-module_name="Navigation"
                  data-design-module_no="00"
                  data-qa-tag="mobile-findadoctor-button"
                  {...secondaryNavList.findDoctor}
                />
              </div>
            </>
          )}
          <div
            className={styles.subitemList}
            data-qa-tag="mobile-sub-item-list"
          >
            <button
              data-trigger-mobile-id={modalISIId}
              onClick={(event) => {
                closeMobileMenu();
                modalISIHandler(event);
              }}
              className={styles.topNavigation}
              data-design-category="top_nav_vd"
              data-design-type="clickAction"
              data-design-action="Expand"
              data-design-nori_id="Navigation"
              data-design-module_name="Navigation"
              data-design-module_no="00"
              data-qa-tag="mobile-importantSafety-button"
              {...secondaryNavList.importantSafety}
            />
            <Link
              onClick={() => closeMobileMenu()}
              href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_pi.pdf"
              target="_blank"
              rel="noopener"
              data-design-type="clickAction"
              data-design-category="top_nav_vd"
              data-design-action="Download"
              data-design-nori_id="Navigation"
              data-design-module_name="Navigation"
              data-design-module_no="00"
              data-qa-tag="mobile-prescribingInfo-link"
              {...secondaryNavList.prescribingInfo}
            />

            {!basic && (
              <button
                data-trigger-mobile-id={modalHealthcareId}
                onClick={(event) => {
                  modalHealthcareHandler(event);
                  closeMobileMenu();
                }}
                className={styles.topNavigation}
                data-design-type="clickAction"
                data-design-category="top_nav_vd"
                data-design-action="Expand"
                data-design-nori_id="Navigation"
                data-design-module_name="Navigation"
                data-design-module_no="00"
                data-qa-tag="mobile-healthcare-button"
                {...secondaryNavList.healthcare}
              />
            )}
            <Link
              onClick={() => closeMobileMenu()}
              href="https://www.organon.com/product/usa/pi_circulars/n/nexplanon/nexplanon_ppi.pdf"
              target="_blank"
              rel="noopener"
              data-design-type="clickAction"
              data-design-category="top_nav_vd"
              data-design-action="Download"
              data-design-nori_id="Navigation"
              data-design-module_name="Navigation"
              data-design-module_no="00"
              data-qa-tag="mobile-patientInfo-link"
              {...secondaryNavList.patientInfo}
            />

            {!basic && (
              <Link
                onClick={() => closeMobileMenu()}
                href="/es-us"
                target="_blank"
                rel="noopener"
                data-design-type="clickAction"
                data-design-category="top_nav_vd"
                data-design-action="Link"
                data-design-nori_id="Navigation"
                data-design-module_name="Navigation"
                data-design-module_no="00"
                data-qa-tag="mobile-es-lang-link"
                {...secondaryNavList.lang}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AltMobileMenu;
